import { portalText } from "../../../i18n/portal.js";
/**
 * The console's half of "Start from a shared configuration" — plan-014 GAP (B5's hand-off; FR12-SHR-02). Over
 * `listSharedVoiceAgentConfigurations` (`GET /v1/workspace/shared-agent-configurations`); the copy itself is
 * `createVoiceAgent` with `resource_share_id` (the draft's `resourceShareId`).
 *
 * OPTIONAL ON THE GATEWAY, like the operator-action confirmation: merged onto `ConsoleGateway` here and the API
 * binding spread into `apiGateway`. The sandbox has no second workspace to share from, so it carries none, and the
 * template picker shows no "shared with this workspace" block.
 *
 * Until the api-client lock holder regenerates the client the operation is read through a structural view (B5's
 * precedent in `../../../api/slice/resource-share.ts`): a client that lacks it raises naming the regeneration.
 */
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";

import { readArray, readBoolean, readRecord, readText, readWholeNumber } from "../../../api/decode.js";
import { answeringChoiceOfWire, specialDateListOfWire } from "../../../api/slice/voice-agent-edit.js";

/** A configuration this workspace accepted and may start a new agent from. */
export interface SharedVoiceAgentConfigurationView {
  readonly resourceShareId: string;
  readonly sourceWorkspaceName: string;
  readonly sourceVoiceAgentName: string;
  /** The pinned version's label ("v4"); `null` when it has none. */
  readonly sourceAgentVersionLabel: string | null;
  readonly agentTemplateCode: string;
  readonly languageCode: readonly string[];
  readonly voiceId: string;
  readonly greeting: string;
  readonly channelUnitCount: number;
  readonly recordingEnabled: boolean;
}

declare module "../../../api/gateway.js" {
  interface ConsoleGateway {
    /** The shared configurations this workspace may copy (accepted, not yet copied, the share standing). */
    readonly listSharedVoiceAgentConfigurations?: () => Promise<readonly SharedVoiceAgentConfigurationView[]>;
  }
}

interface SharedConfigurationOperation {
  listSharedVoiceAgentConfigurations(): Promise<ApiResult>;
}

function operationOf(client: WeTalkApiClient): SharedConfigurationOperation {
  const held = (client as unknown as Partial<Record<"listSharedVoiceAgentConfigurations", unknown>>)["listSharedVoiceAgentConfigurations"];
  return {
    listSharedVoiceAgentConfigurations: () => {
      if (typeof held !== "function") {
        throw new Error(
          portalText("operations", "the_generated_api_client_has_no_listsharedvoiceagentconfig_dbc57833"),
        );
      }
      return (held as () => Promise<ApiResult>).call(client);
    },
  };
}

/** One row of the wire answer. */
export function sharedVoiceAgentConfigurationOf(value: unknown, path: string): SharedVoiceAgentConfigurationView {
  const row = readRecord(value, path);
  const label = row["source_agent_version_label"];
  return {
    resourceShareId: readText(row["resource_share_id"], `${path}.resource_share_id`),
    sourceWorkspaceName: readText(row["source_workspace_name"], `${path}.source_workspace_name`),
    sourceVoiceAgentName: readText(row["source_voice_agent_name"], `${path}.source_voice_agent_name`),
    sourceAgentVersionLabel: label === null ? null : readText(label, `${path}.source_agent_version_label`),
    agentTemplateCode: readText(row["agent_template_code"], `${path}.agent_template_code`),
    languageCode: readArray(row["language_code"], `${path}.language_code`).map((one, index) => readText(one, `${path}.language_code[${String(index)}]`)),
    voiceId: readText(row["voice_id"], `${path}.voice_id`),
    greeting: readText(row["greeting"], `${path}.greeting`),
    channelUnitCount: readWholeNumber(row["channel_unit_count"], `${path}.channel_unit_count`),
    recordingEnabled: readBoolean(row["recording_enabled"], `${path}.recording_enabled`),
    // plan-015 (G1U's note; AGC-UI's answerFromShared reads them): the pinned version's voices and answering answers.
    voiceByLanguage: Object.fromEntries(
      Object.entries(readRecord(row["voice_by_language"], `${path}.voice_by_language`)).map(([code, voiceId]) => [
        code,
        readText(voiceId, `${path}.voice_by_language.${code}`),
      ]),
    ),
    timeZone: row["time_zone"] === null ? null : readText(row["time_zone"], `${path}.time_zone`),
    specialDate: specialDateListOfWire(row["special_date"], `${path}.special_date`),
    answeringChoice: answeringChoiceOfWire(row, path),
  };
}

/** The API binding, spread into `apiGateway` beside the other slices. */
export function sharedConfigurationApiSlice(client: WeTalkApiClient): {
  readonly listSharedVoiceAgentConfigurations: () => Promise<readonly SharedVoiceAgentConfigurationView[]>;
} {
  const operation = operationOf(client);
  return {
    async listSharedVoiceAgentConfigurations() {
      const body = readRecord(assertOk(await operation.listSharedVoiceAgentConfigurations()), "listSharedVoiceAgentConfigurations");
      return readArray(body["data"], "listSharedVoiceAgentConfigurations.data").map((one, index) =>
        sharedVoiceAgentConfigurationOf(one, `listSharedVoiceAgentConfigurations.data[${String(index)}]`),
      );
    },
  };
}

/** The wizard's query key, beside the template picker's. */
export const SHARED_CONFIGURATION_QUERY_KEY = ["wizard", "shared-configuration"] as const;
